import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './Home2.css';

const DEMO_VISION = 'Launch a profitable SaaS in 18 months';

const DEMO_PLAN = {
  milestones: [
    { id: 'm1', title: 'Validate problem with 20 founders', status: 'standby', progress: 22 },
    {
      id: 'm2', title: 'Ship MVP and get first signup',     status: 'active',  progress: 64,
      actions: [
        {
          id: 'a1', title: 'Build the landing page', count: 3,
          subs: [
            { title: 'Write hero copy that converts',     done: true  },
            { title: 'Design pricing card with 3 tiers',  done: false, focus: true },
            { title: 'Wire up waitlist form to Supabase', done: false },
          ],
        },
        { id: 'a2', title: 'Ship core onboarding flow',  count: 4 },
        { id: 'a3', title: 'Connect Stripe + checkout',   count: 2 },
      ],
    },
    { id: 'm3', title: 'First 10 paying customers',        status: 'standby', progress: 0 },
    { id: 'm4', title: 'Hit $5K MRR and product-market fit', status: 'standby', progress: 0 },
  ],
};

const TODAY_ITEMS = [
  { title: 'Write hero copy that converts',     done: true,  pinned: true,  src: 'Landing page' },
  { title: 'Design pricing card with 3 tiers',  done: false, pinned: true,  src: 'Landing page', focus: true },
  { title: 'Wire up waitlist form to Supabase', done: false, pinned: false, src: 'Landing page' },
  { title: 'Draft onboarding email sequence',   done: false, pinned: false, src: 'Onboarding flow' },
  { title: 'Test Stripe webhook locally',       done: false, pinned: false, src: 'Stripe checkout', stale: true },
];

const STEPS = ['vision', 'plan', 'expand', 'detail', 'agent', 'execute', 'done'];

const CONN_H = 52;

function DemoConnector({ sourceRef, childRefs, color = 'rgba(247,244,238,0.5)', dep, scrollRef }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState({ source: null, children: [] });

  useEffect(() => {
    let raf1, raf2;
    const measure = () => {
      if (!sourceRef?.current || !containerRef.current) return;
      const base = containerRef.current.getBoundingClientRect();
      const sr = sourceRef.current.getBoundingClientRect();
      const sourceX = sr.left - base.left + sr.width / 2;
      const children = (childRefs?.current || [])
        .map(el => {
          if (!el) return null;
          const r = el.getBoundingClientRect();
          return r.left - base.left + r.width / 2;
        })
        .filter(x => x !== null);
      setPos({ source: sourceX, children });
    };
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });
    window.addEventListener('resize', measure);
    const scrollEl = scrollRef?.current;
    if (scrollEl) scrollEl.addEventListener('scroll', measure, { passive: true });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', measure);
      if (scrollEl) scrollEl.removeEventListener('scroll', measure);
    };
  }, [sourceRef, childRefs, dep, scrollRef]);

  const midY = CONN_H / 2;
  const valid = pos.children;
  const minX = pos.source != null && valid.length ? Math.min(pos.source, ...valid) : pos.source;
  const maxX = pos.source != null && valid.length ? Math.max(pos.source, ...valid) : pos.source;

  return (
    <div
      className="h2-demo-conn"
      ref={containerRef}
      style={{ height: CONN_H, '--conn-color': color }}
    >
      {pos.source != null && (
        <svg
          style={{ position: 'absolute', inset: 0, width: '100%', height: CONN_H }}
          overflow="visible"
        >
          {/* glow halo behind lines */}
          <defs>
            <filter id={`glow-${color.replace(/[^a-z0-9]/gi, '')}`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* trunk: source down to mid */}
          <line
            x1={pos.source} y1={0}
            x2={pos.source} y2={midY}
            stroke={color}
            strokeWidth={1.5}
            strokeDasharray="5 4"
            strokeLinecap="round"
          />
          {/* horizontal bridge */}
          {valid.length > 0 && (
            <line
              x1={minX} y1={midY}
              x2={maxX} y2={midY}
              stroke={color}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              strokeLinecap="round"
            />
          )}
          {/* branches to each child */}
          {valid.map((cx, i) => (
            <line
              key={i}
              x1={cx} y1={midY}
              x2={cx} y2={CONN_H}
              stroke={color}
              strokeWidth={1.5}
              strokeDasharray="5 4"
              strokeLinecap="round"
            />
          ))}
          {/* end dots at children */}
          {valid.map((cx, i) => (
            <circle key={`d${i}`} cx={cx} cy={CONN_H} r={2.8} fill={color} />
          ))}
          {/* source dot */}
          <circle cx={pos.source} cy={0} r={2.8} fill={color} />
          {/* junction dot at bridge */}
          {valid.length > 1 && (
            <circle cx={pos.source} cy={midY} r={2} fill={color} />
          )}
        </svg>
      )}
    </div>
  );
}

function InteractiveDemo() {
  // 'idle' → 'typing' → 'generating' → 'plan' → 'expand' → 'detail' → 'agent' → 'execute' → 'done'
  const [step, setStep] = useState('idle');
  const [view, setView] = useState('planner'); // planner | execute
  const [visionText, setVisionText] = useState('');
  const [revealedMs, setRevealedMs] = useState(0);
  const [revealedAc, setRevealedAc] = useState(0);
  const [revealedSub, setRevealedSub] = useState(0);
  const [revealedExec, setRevealedExec] = useState(0);
  const timeouts = useRef([]);

  // Refs for SVG branch connectors (mirror real planner)
  const visionRef = useRef(null);
  const msRefs    = useRef([]);  // 4 milestone cards
  const acRefs    = useRef([]);  // 3 action cards
  const subRefs   = useRef([]);  // 3 sub-step cards
  const msRowRef  = useRef(null);
  const acRowRef  = useRef(null);
  const subRowRef = useRef(null);
  const focusedMsRef = useRef(null);
  const focusedAcRef = useRef(null);

  // Auto-scroll latest revealed card into view
  useEffect(() => {
    if (revealedMs > 0 && msRowRef.current) {
      const card = msRefs.current[revealedMs - 1];
      if (card) {
        msRowRef.current.scrollTo({
          left: card.offsetLeft - 16,
          behavior: 'smooth',
        });
      }
    }
  }, [revealedMs]);

  useEffect(() => {
    if (revealedAc > 0 && acRowRef.current) {
      const card = acRefs.current[revealedAc - 1];
      if (card) {
        acRowRef.current.scrollTo({
          left: card.offsetLeft - 16,
          behavior: 'smooth',
        });
      }
    }
  }, [revealedAc]);

  useEffect(() => {
    if (revealedSub > 0 && subRowRef.current) {
      const card = subRefs.current[revealedSub - 1];
      if (card) {
        subRowRef.current.scrollTo({
          left: card.offsetLeft - 16,
          behavior: 'smooth',
        });
      }
    }
  }, [revealedSub]);

  // Reset parent rows to start when a new level begins
  useEffect(() => {
    if (step === 'expand') {
      msRowRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
    }
    if (step === 'detail') {
      msRowRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
      acRowRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
    }
    if (step === 'agent') {
      subRowRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }, [step]);

  const clearTimers = () => {
    timeouts.current.forEach(clearTimeout);
    timeouts.current = [];
  };
  useEffect(() => clearTimers, []);

  const start = () => {
    clearTimers();
    setStep('typing');
    setView('planner');
    setVisionText('');
    setRevealedMs(0);
    setRevealedAc(0);
    setRevealedSub(0);
    setRevealedExec(0);
    let i = 0;
    const typeNext = () => {
      if (i < DEMO_VISION.length) {
        setVisionText(DEMO_VISION.slice(0, i + 1));
        i++;
        timeouts.current.push(setTimeout(typeNext, 55));
      } else {
        timeouts.current.push(setTimeout(() => setStep('generating'), 900));
      }
    };
    typeNext();
  };

  const reset = () => {
    clearTimers();
    setStep('idle');
    setView('planner');
    setVisionText('');
    setRevealedMs(0);
    setRevealedAc(0);
    setRevealedSub(0);
    setRevealedExec(0);
  };

  // Auto-progress driver
  useEffect(() => {
    if (step === 'generating') {
      timeouts.current.push(setTimeout(() => setStep('plan'), 2400));
    }
    if (step === 'expand') {
      const ms = DEMO_PLAN.milestones[1];
      if (revealedAc < ms.actions.length) {
        timeouts.current.push(setTimeout(() => setRevealedAc(n => n + 1), 650));
      } else {
        timeouts.current.push(setTimeout(() => setStep('detail'), 1400));
      }
    }
    if (step === 'detail') {
      const action = DEMO_PLAN.milestones[1].actions[0];
      if (revealedSub < action.subs.length) {
        timeouts.current.push(setTimeout(() => setRevealedSub(n => n + 1), 600));
      } else {
        timeouts.current.push(setTimeout(() => setStep('agent'), 1600));
      }
    }
    if (step === 'agent') {
      timeouts.current.push(setTimeout(() => {
        setStep('execute');
        setView('execute');
      }, 5000));
    }
    if (step === 'execute') {
      if (revealedExec < TODAY_ITEMS.length) {
        timeouts.current.push(setTimeout(() => setRevealedExec(n => n + 1), 450));
      } else {
        timeouts.current.push(setTimeout(() => setStep('done'), 2800));
      }
    }
  }, [step, revealedAc, revealedSub, revealedExec]);

  // Reveal milestones during 'plan' step
  useEffect(() => {
    if (step === 'plan') {
      if (revealedMs < DEMO_PLAN.milestones.length) {
        timeouts.current.push(setTimeout(() => setRevealedMs(n => n + 1), 700));
      } else {
        timeouts.current.push(setTimeout(() => setStep('expand'), 1400));
      }
    }
  }, [step, revealedMs]);

  const stepIdx = STEPS.indexOf(
    step === 'idle' ? 'vision' :
    step === 'typing' ? 'vision' :
    step === 'generating' ? 'vision' :
    step
  );

  return (
    <section className="h2-demo">
      <div className="h2-demo-inner">
        <div className="h2-demo-head h2-reveal">
          <h2 className="h2-section-heading">
            Type one line. <span className="h2-accent">Get a plan.</span>
          </h2>
          <p className="h2-section-body">
            No blank canvas. No setup grind. Hit start and see what dmyor does with a single sentence — milestones, actions, sub-steps, all the way down to today's first move.
          </p>
        </div>

        {/* Step indicator */}
        <div className="h2-demo-stepper h2-reveal">
          {[
            { key: 'vision',  label: 'Vision'     },
            { key: 'plan',    label: 'Milestones' },
            { key: 'expand',  label: 'Actions'    },
            { key: 'detail',  label: 'Sub-steps'  },
            { key: 'agent',   label: 'Agent'      },
            { key: 'execute', label: 'Execute'    },
          ].map((s, i) => (
            <div
              key={s.key}
              className={`h2-step-pill ${stepIdx >= STEPS.indexOf(s.key) && step !== 'idle' ? 'h2-step-pill--done' : ''} ${STEPS[stepIdx] === s.key ? 'h2-step-pill--active' : ''}`}
            >
              <span className="h2-step-num">{i + 1}</span>
              <span className="h2-step-label">{s.label}</span>
            </div>
          ))}
        </div>

        <div className="h2-demo-stage h2-reveal">
          {/* Stage chrome (tabs flip when execute fires) */}
          <div className="h2-demo-chrome">
            <span className="h2-demo-back">← Visions</span>
            <div className="h2-demo-tabs">
              <span className={`h2-demo-tab ${view === 'planner' ? 'h2-demo-tab--active' : ''}`}>☰ Planner</span>
              <span className={`h2-demo-tab ${view === 'execute' ? 'h2-demo-tab--active' : ''}`}>▶ Execute</span>
              <span className="h2-demo-tab">✦ Journal</span>
            </div>
            <span className="h2-demo-reset" onClick={reset} role="button">↻ Reset</span>
          </div>

          {/* IDLE */}
          {step === 'idle' && (
            <div className="h2-demo-empty">
              <button className="h2-demo-add-btn" onClick={start}>
                <span className="h2-demo-plus">+</span>
                <span>Add your first vision</span>
              </button>
              <p className="h2-demo-empty-hint">We'll build a real 4-level plan in seconds.</p>
            </div>
          )}

          {/* TYPING */}
          {step === 'typing' && (
            <div className="h2-demo-prompt">
              <span className="h2-demo-step-tag">Step 1 · Write the vision</span>
              <div className="h2-demo-input">
                <span className="h2-demo-input-text">{visionText}</span>
                <span className="h2-demo-cursor" />
              </div>
              <p className="h2-demo-prompt-hint">Anything you want to build, become, or finish.</p>
            </div>
          )}

          {/* GENERATING */}
          {step === 'generating' && (
            <div className="h2-demo-generating">
              <span className="h2-demo-step-tag">Step 2 · AI building your plan</span>
              <div className="h2-demo-thinking">
                <span className="h2-demo-thinking-dot" />
                <span className="h2-demo-thinking-dot" />
                <span className="h2-demo-thinking-dot" />
                <span className="h2-demo-thinking-label">Breaking it into 4 levels…</span>
              </div>
              <div className="h2-demo-vision-card h2-demo-vision-card--lg">
                <span className="h2-prev-eyebrow">VISION</span>
                <span className="h2-prev-vision-title">{DEMO_VISION}</span>
              </div>
            </div>
          )}

          {/* PLANNER VIEW with progressive levels */}
          {view === 'planner' && ['plan', 'expand', 'detail', 'agent'].includes(step) && (
            <div className="h2-demo-canvas">
              {/* Vision (level 1) */}
              <div ref={visionRef} className="h2-demo-vision-card h2-demo-vision-card--lg">
                <span className="h2-prev-eyebrow">VISION · 18 MONTHS</span>
                <span className="h2-prev-vision-title">{DEMO_VISION}</span>
              </div>

              {/* Connector: vision → milestones */}
              <DemoConnector
                sourceRef={visionRef}
                childRefs={msRefs}
                color="rgba(247,244,238,0.5)"
                dep={`ms-${revealedMs}-${step}`}
                scrollRef={msRowRef}
              />

              {/* Milestones (level 2) */}
              <div ref={msRowRef} className="h2-demo-ms-row">
                {DEMO_PLAN.milestones.map((m, i) => {
                  const isFocus = m.id === 'm2' && (step === 'expand' || step === 'detail' || step === 'agent');
                  return (
                    <div
                      key={m.id}
                      ref={el => {
                        msRefs.current[i] = el;
                        if (isFocus) focusedMsRef.current = el;
                      }}
                      className={`h2-demo-ms-card ${i < revealedMs ? 'h2-demo-pop-in' : 'h2-demo-hidden'} ${isFocus ? 'h2-demo-card--focus' : ''}`}
                    >
                      <span className="h2-prev-eyebrow">MILESTONE</span>
                      <span className="h2-demo-ms-title">{m.title}</span>
                      <div className="h2-prev-bar">
                        <div
                          className="h2-prev-bar-fill"
                          style={{ width: `${m.progress}%`, background: m.status === 'active' ? '#34d399' : '#a78bfa' }}
                        />
                      </div>
                      <span className={`h2-prev-status h2-prev-status--${m.status}`}>
                        {m.status === 'active' ? 'Active' : 'Standby'}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Actions (level 3) */}
              {(step === 'expand' || step === 'detail' || step === 'agent') && (
                <>
                  <DemoConnector
                    sourceRef={focusedMsRef}
                    childRefs={acRefs}
                    color="rgba(52,211,153,0.55)"
                    dep={`ac-${revealedAc}-${step}`}
                    scrollRef={acRowRef}
                  />
                  <div ref={acRowRef} className="h2-demo-ac-row">
                    {DEMO_PLAN.milestones[1].actions.map((a, i) => {
                      const isFocus = a.id === 'a1' && (step === 'detail' || step === 'agent');
                      return (
                        <div
                          key={a.id}
                          ref={el => {
                            acRefs.current[i] = el;
                            if (isFocus) focusedAcRef.current = el;
                          }}
                          className={`h2-demo-ac-card-lg ${i < revealedAc ? 'h2-demo-pop-in' : 'h2-demo-hidden'} ${isFocus ? 'h2-demo-card--focus' : ''}`}
                        >
                          <span className="h2-prev-eyebrow">ACTION</span>
                          <span className="h2-demo-ac-title">{a.title}</span>
                          <span className="h2-demo-ac-meta">{a.count} sub-steps</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Sub-steps (level 4) */}
              {(step === 'detail' || step === 'agent') && (
                <>
                  <DemoConnector
                    sourceRef={focusedAcRef}
                    childRefs={subRefs}
                    color="rgba(96,165,250,0.55)"
                    dep={`sub-${revealedSub}-${step}`}
                    scrollRef={subRowRef}
                  />
                  <div ref={subRowRef} className="h2-demo-sub-row">
                    {DEMO_PLAN.milestones[1].actions[0].subs.map((s, i) => (
                      <div
                        key={i}
                        ref={el => { subRefs.current[i] = el; }}
                        className={`h2-demo-sub-card ${i < revealedSub ? 'h2-demo-pop-in' : 'h2-demo-hidden'} ${s.focus ? 'h2-demo-card--focus' : ''}`}
                      >
                        <span className={`h2-demo-sub-check ${s.done ? 'h2-demo-sub-check--done' : ''}`}>
                          {s.done && '✓'}
                        </span>
                        <span className={s.done ? 'h2-demo-sub-text--done' : ''}>{s.title}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Agent pop */}
              {step === 'agent' && (
                <div className="h2-demo-chat-pop h2-demo-pop-in">
                  <div className="h2-demo-chat-head">
                    <span className="h2-demo-chat-spark">✦</span>
                    <span>Agent · focused on "Build the landing page"</span>
                  </div>
                  <p>
                    Hero copy is done. <strong>Pricing card</strong> is the bottleneck — it gates the waitlist form's value prop. Ship the pricing first, then the form unlocks naturally. I can draft three tier variants if you want.
                  </p>
                  <div className="h2-demo-chat-suggest">
                    <span className="h2-demo-chat-suggest-tag">SUGGESTION</span>
                    <span>+ Add sub-step: "A/B test pricing tiers after launch"</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EXECUTE VIEW */}
          {view === 'execute' && (step === 'execute' || step === 'done') && (
            <div className="h2-demo-execute">
              <div className="h2-demo-exec-hero">
                <div>
                  <h3 className="h2-demo-exec-title">Today</h3>
                  <p className="h2-demo-exec-sub">5 actionable nodes across your plan</p>
                </div>
                <div className="h2-demo-exec-stats">
                  <div className="h2-demo-exec-stat">
                    <strong>5</strong>
                    <span>Nodes</span>
                  </div>
                  <div className="h2-demo-exec-stat">
                    <strong>2</strong>
                    <span>Pinned</span>
                  </div>
                  <div className="h2-demo-exec-stat">
                    <strong>517</strong>
                    <span>Days left</span>
                  </div>
                </div>
              </div>

              <div className="h2-demo-exec-filters">
                <span className="h2-demo-exec-filter h2-demo-exec-filter--active">All <span>5</span></span>
                <span className="h2-demo-exec-filter">Pinned <span>2</span></span>
                <span className="h2-demo-exec-filter">Due today <span>3</span></span>
                <span className="h2-demo-exec-filter">Stale <span>1</span></span>
              </div>

              <div className="h2-demo-exec-list">
                {TODAY_ITEMS.map((it, i) => (
                  <div
                    key={i}
                    className={`h2-demo-exec-item ${i < revealedExec ? 'h2-demo-pop-in' : 'h2-demo-hidden'} ${it.focus ? 'h2-demo-card--focus' : ''}`}
                  >
                    <span className={`h2-demo-exec-check ${it.done ? 'h2-demo-exec-check--done' : ''}`}>
                      {it.done && '✓'}
                    </span>
                    <div className="h2-demo-exec-body">
                      <span className={`h2-demo-exec-text ${it.done ? 'h2-demo-exec-text--done' : ''}`}>
                        {it.title}
                      </span>
                      <span className="h2-demo-exec-src">
                        {it.src}
                        {it.stale && <span className="h2-demo-exec-stale">· stale 6d</span>}
                      </span>
                    </div>
                    {it.pinned && <span className="h2-demo-exec-pin" title="Pinned">📌</span>}
                  </div>
                ))}
              </div>

              <div className="h2-demo-exec-log">
                <span className="h2-demo-exec-log-label">Quick log</span>
                <div className="h2-demo-exec-log-input">
                  <span>What did you finish today?</span>
                  <span className="h2-demo-exec-log-cta">Save →</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Callouts after done */}
        {step === 'done' && (
          <div className="h2-demo-callouts h2-demo-fade-in">
            <div className="h2-demo-callout">
              <span className="h2-demo-callout-num">01</span>
              <strong>Four levels deep, always coherent.</strong>
              <p>Vision → Milestones → Actions → Sub-steps. Progress rolls up automatically.</p>
            </div>
            <div className="h2-demo-callout">
              <span className="h2-demo-callout-num">02</span>
              <strong>Agent reads the whole tree.</strong>
              <p>Attach any node, ask anything. Suggestions you can accept with one click.</p>
            </div>
            <div className="h2-demo-callout">
              <span className="h2-demo-callout-num">03</span>
              <strong>Execute shows only today.</strong>
              <p>Pin, filter, check off, log. Forget the tree — just ship.</p>
            </div>
          </div>
        )}

        <div className="h2-demo-cta h2-reveal">
          {step === 'idle' ? (
            <button className="h2-btn-primary h2-btn-large" onClick={start}>Run the demo →</button>
          ) : (
            <Link to="/dashboard/planning/new" className="h2-btn-primary h2-btn-large">Build your real plan →</Link>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Mini graphics for each function ─────────────────────────────────────────

function GraphicDepth() {
  return (
    <div className="h2-g h2-g-depth">
      <div className="h2-g-mini-nav">
        <span>☰ Planner</span>
        <span className="h2-g-mini-tab">4 levels</span>
      </div>
      <div className="h2-g-card h2-g-card--vision">
        <div className="h2-g-card-head">
          <span className="h2-g-eyebrow">VISION</span>
          <span className="h2-g-dot h2-g-dot--white" />
        </div>
        <span className="h2-g-title">Launch a profitable SaaS</span>
        <div className="h2-g-bar"><div className="h2-g-bar-fill h2-g-bar-fill--white" style={{ width: '34%' }} /></div>
      </div>
      <div className="h2-g-conn" />
      <div className="h2-g-card h2-g-card--ms">
        <div className="h2-g-card-head">
          <span className="h2-g-eyebrow">MILESTONE</span>
          <span className="h2-g-dot h2-g-dot--violet" />
        </div>
        <span className="h2-g-title">Ship MVP</span>
        <div className="h2-g-bar"><div className="h2-g-bar-fill h2-g-bar-fill--violet" style={{ width: '64%' }} /></div>
      </div>
      <div className="h2-g-conn" />
      <div className="h2-g-card h2-g-card--ac">
        <div className="h2-g-card-head">
          <span className="h2-g-eyebrow">ACTION</span>
          <span className="h2-g-dot h2-g-dot--green" />
        </div>
        <span className="h2-g-title">Build landing page</span>
        <span className="h2-g-meta">3 sub-steps · 1 done</span>
      </div>
      <div className="h2-g-conn" />
      <div className="h2-g-card h2-g-card--sub">
        <span className="h2-g-sub-dot" />
        <span className="h2-g-sub-text">Design pricing card</span>
      </div>
    </div>
  );
}

function GraphicMemory() {
  return (
    <div className="h2-g h2-g-chat">
      <div className="h2-g-mini-nav">
        <span>✦ AI</span>
        <span className="h2-g-mini-tab">full context</span>
      </div>
      <div className="h2-g-context-pill">
        <span className="h2-g-ctx-dot" />
        <span>Knows 1 vision · 4 milestones · 12 nodes</span>
      </div>
      <div className="h2-g-bubble h2-g-bubble--user">
        <span className="h2-g-attach">
          <span className="h2-g-attach-dot" />
          Ship MVP
        </span>
        <p>What's actually blocking this?</p>
        <span className="h2-g-time">2:14 PM</span>
      </div>
      <div className="h2-g-bubble h2-g-bubble--ai">
        <p>Hero copy is done. <strong>Pricing card</strong> is the bottleneck — it gates the waitlist form's value prop.</p>
        <span className="h2-g-time">2:14 PM</span>
      </div>
      <div className="h2-g-input">
        <span className="h2-g-input-text">Ask anything…</span>
        <span className="h2-g-input-send">↑</span>
      </div>
    </div>
  );
}

function GraphicSuggest() {
  return (
    <div className="h2-g h2-g-suggest">
      <div className="h2-g-mini-nav">
        <span>✦ AI</span>
        <span className="h2-g-mini-tab">3 ideas</span>
      </div>

      <div className="h2-g-parent-pill">
        <span className="h2-g-eyebrow">UNDER</span>
        <span>Build landing page</span>
      </div>

      <div className="h2-g-sug-card">
        <div className="h2-g-sug-head">
          <span className="h2-g-sug-type">+ SUB-STEP</span>
          <span className="h2-g-sug-count">1 / 3</span>
        </div>
        <p className="h2-g-sug-text">A/B test pricing tiers <em>after launch</em>.</p>
        <p className="h2-g-sug-reason">Why: hero copy will likely shift conversion. Test data tells you which tier converts.</p>
        <div className="h2-g-sug-btns">
          <span className="h2-g-sug-accept">+ Add</span>
          <span className="h2-g-sug-skip">Skip</span>
        </div>
      </div>

      <div className="h2-g-sug-mini">
        <span className="h2-g-sug-mini-tag">+ SUB-STEP</span>
        <span>Write 3-tier breakdown copy</span>
      </div>
      <div className="h2-g-sug-mini">
        <span className="h2-g-sug-mini-tag">+ ACTION</span>
        <span>Set up Plausible analytics</span>
      </div>
    </div>
  );
}

function GraphicToday() {
  return (
    <div className="h2-g h2-g-today">
      <div className="h2-g-today-head">
        <h4>Today</h4>
        <span className="h2-g-today-stat"><strong>5</strong> nodes</span>
      </div>
      <div className="h2-g-today-filters">
        <span className="h2-g-filter h2-g-filter--active">All</span>
        <span className="h2-g-filter">Pinned</span>
        <span className="h2-g-filter">Due</span>
      </div>
      <div className="h2-g-today-item">
        <span className="h2-g-check h2-g-check--done">✓</span>
        <span className="h2-g-today-done">Write hero copy</span>
      </div>
      <div className="h2-g-today-item h2-g-today-item--focus">
        <span className="h2-g-check" />
        <span>Design pricing card</span>
        <span className="h2-g-pin">📌</span>
      </div>
      <div className="h2-g-today-item">
        <span className="h2-g-check" />
        <span>Wire up Stripe</span>
      </div>
    </div>
  );
}

function GraphicDrift() {
  return (
    <div className="h2-g h2-g-drift">
      <div className="h2-g-drift-card">
        <div className="h2-g-drift-row">
          <span className="h2-g-eyebrow">MILESTONE</span>
          <span className="h2-g-stale">stale 6d</span>
        </div>
        <span className="h2-g-title">First 10 paying users</span>
        <div className="h2-g-bar"><div className="h2-g-bar-fill" style={{ width: '24%' }} /></div>
        <span className="h2-g-drift-note">Below pace · last update 6 days ago</span>
      </div>
      <div className="h2-g-drift-card h2-g-drift-card--healthy">
        <span className="h2-g-eyebrow">MILESTONE</span>
        <span className="h2-g-title">Ship MVP</span>
        <div className="h2-g-bar"><div className="h2-g-bar-fill h2-g-bar-fill--green" style={{ width: '72%' }} /></div>
        <span className="h2-g-drift-ok">On pace</span>
      </div>
    </div>
  );
}

function GraphicJournal() {
  return (
    <div className="h2-g h2-g-journal">
      <div className="h2-g-mini-nav">
        <span>✦ Journal</span>
        <span className="h2-g-mini-tab">Apr 2026</span>
      </div>

      <div className="h2-g-jr-entry">
        <div className="h2-g-jr-row">
          <span className="h2-g-jr-date">Apr 19</span>
          <span className="h2-g-jr-mood h2-g-jr-mood--win">win</span>
        </div>
        <span className="h2-g-jr-node">↳ Build landing page</span>
        <p>First paying client signed. Six week program. This is actually a thing now.</p>
      </div>

      <div className="h2-g-jr-entry">
        <div className="h2-g-jr-row">
          <span className="h2-g-jr-date">Mar 04</span>
          <span className="h2-g-jr-mood h2-g-jr-mood--note">note</span>
        </div>
        <span className="h2-g-jr-node">↳ Talk to founders</span>
        <p>First post hit 400 likes. Three DMs about pricing.</p>
      </div>

      <div className="h2-g-jr-entry h2-g-jr-entry--faint">
        <div className="h2-g-jr-row">
          <span className="h2-g-jr-date">Feb 12</span>
        </div>
        <span className="h2-g-jr-node">↳ Validate problem</span>
        <p>Did the first 5 calls. Same pattern every time.</p>
      </div>
    </div>
  );
}

const FUNCTIONS = [
  {
    tab: 'Depth',
    title: 'Break your vision into actionable milestones with AI.',
    desc:  'Vision becomes milestones. Milestones become actions. Actions become the next 15 minutes. AI does the breakdown — you see it all without losing where you are.',
    Graphic: GraphicDepth,
  },
  {
    tab: 'Intelligent',
    title: 'An AI that already knows your story.',
    desc:  'Your vision, every node, every update — already in its memory. Ask anything. You\'ll never have to explain yourself twice.',
    Graphic: GraphicMemory,
  },
  {
    tab: 'Suggest',
    title: 'It tells you the next move.',
    desc:  'Stuck? It finds the bottleneck. Drafts the next sub-step. You accept with one click and keep going.',
    Graphic: GraphicSuggest,
  },
  {
    tab: 'Record',
    title: 'Write the story of how it got built.',
    desc:  'A journal tied to the work itself. A year in, you\'ll have proof of every decision, every win, every quiet stretch.',
    Graphic: GraphicJournal,
  },
];

function FunctionsSwitcher() {
  const [active, setActive] = useState(0);
  const Graphic = FUNCTIONS[active].Graphic;
  return (
    <div className="h2-fn-switcher">
      <div className="h2-fn-tabs" role="tablist">
        {FUNCTIONS.map((f, i) => (
          <button
            key={f.tab}
            role="tab"
            className={`h2-fn-tab ${active === i ? 'h2-fn-tab--active' : ''}`}
            onClick={() => setActive(i)}
          >
            <span className="h2-fn-tab-num">{String(i + 1).padStart(2, '0')}</span>
            <span className="h2-fn-tab-label">{f.tab}</span>
          </button>
        ))}
      </div>

      <div className="h2-fn-panel">
        <div className="h2-fn-panel-text" key={`text-${active}`}>
          <h3>{FUNCTIONS[active].title}</h3>
          <p>{FUNCTIONS[active].desc}</p>
        </div>
        <div className="h2-fn-panel-graphic" key={`g-${active}`}>
          <div className="h2-phone">
            <div className="h2-phone-side h2-phone-side--top" />
            <div className="h2-phone-side h2-phone-side--bot1" />
            <div className="h2-phone-side h2-phone-side--bot2" />
            <div className="h2-phone-side h2-phone-side--right" />
            <div className="h2-phone-frame">
              <div className="h2-phone-notch" />
              <div className="h2-phone-screen">
                <div className="h2-phone-status">
                  <span>9:41</span>
                  <span className="h2-phone-status-right">●●●● ▲ 87%</span>
                </div>
                <div className="h2-phone-content">
                  <Graphic />
                </div>
                <div className="h2-phone-home" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Bridge mode graphics (browser-style mockups) ───────────────────────────

function PlanModeGraphic() {
  return (
    <div className="h2-bg-mock">
      <div className="h2-bg-mock-head">
        <div className="h2-bg-mock-dots">
          <span className="h2-bg-mock-dot" />
          <span className="h2-bg-mock-dot" />
          <span className="h2-bg-mock-dot" />
        </div>
        <span className="h2-bg-mock-title">☰ Planner · 1 vision</span>
        <span className="h2-bg-mock-meta">4 levels</span>
      </div>
      <div className="h2-bg-mock-body h2-bg-mock-body--plan">
        {/* Vision card */}
        <div className="h2-pln-vision">
          <span className="h2-pln-vision-tag">VISION</span>
          <span className="h2-pln-vision-text">Launch a profitable SaaS in 18 months</span>
        </div>

        {/* rail label */}
        <span className="h2-pln-rail">MILESTONES ON THE PATH <em>3</em></span>

        {/* connector */}
        <div className="h2-pln-conn" aria-hidden="true">
          <span className="h2-pln-conn-trunk" />
          <span className="h2-pln-conn-bridge" />
          <span className="h2-pln-conn-drop" style={{ left: '16.6%' }} />
          <span className="h2-pln-conn-drop" style={{ left: '50%' }} />
          <span className="h2-pln-conn-drop" style={{ left: '83.3%' }} />
        </div>

        {/* milestone cards */}
        <div className="h2-pln-row">
          <div className="h2-pln-card h2-pln-card--done">
            <div className="h2-pln-card-eyebrow"><span>Milestone</span><span className="h2-pln-card-dot" /></div>
            <span className="h2-pln-card-title">Validate the problem</span>
            <div className="h2-pln-card-prog">
              <div className="h2-pln-card-track"><span style={{ width: '100%' }} /></div>
              <span className="h2-pln-card-count">5/5</span>
            </div>
            <div className="h2-pln-card-footer">
              <span className="h2-pln-card-pill">Done</span>
              <span className="h2-pln-card-acts">☆ ✎</span>
            </div>
          </div>

          <div className="h2-pln-card h2-pln-card--active">
            <div className="h2-pln-card-eyebrow"><span>Milestone</span><span className="h2-pln-card-dot" /></div>
            <span className="h2-pln-card-title">Ship the MVP</span>
            <div className="h2-pln-card-prog">
              <div className="h2-pln-card-track"><span style={{ width: '50%' }} /></div>
              <span className="h2-pln-card-count">3/6</span>
            </div>
            <div className="h2-pln-card-footer">
              <span className="h2-pln-card-pill">Active</span>
              <span className="h2-pln-card-acts">★ ✎</span>
            </div>
          </div>

          <div className="h2-pln-card h2-pln-card--standby">
            <div className="h2-pln-card-eyebrow"><span>Milestone</span><span className="h2-pln-card-dot" /></div>
            <span className="h2-pln-card-title">First 10 customers</span>
            <div className="h2-pln-card-prog">
              <div className="h2-pln-card-track"><span style={{ width: '0%' }} /></div>
              <span className="h2-pln-card-count">0/4</span>
            </div>
            <div className="h2-pln-card-footer">
              <span className="h2-pln-card-pill">Standby</span>
              <span className="h2-pln-card-acts">☆ ✎</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExecuteModeGraphic() {
  return (
    <div className="h2-bg-mock">
      <div className="h2-bg-mock-head">
        <div className="h2-bg-mock-dots">
          <span className="h2-bg-mock-dot" />
          <span className="h2-bg-mock-dot" />
          <span className="h2-bg-mock-dot" />
        </div>
        <span className="h2-bg-mock-title">▶ Execute</span>
        <span className="h2-bg-mock-meta">Today · Fri 6 Jun</span>
      </div>
      <div className="h2-bg-mock-body h2-bg-mock-body--exec">
        <div className="h2-bg-exec-hero">
          <div>
            <h4>Today</h4>
            <p>What's actionable across your plan, right now.</p>
          </div>
        </div>

        <div className="h2-bg-stats">
          <div className="h2-bg-stat">
            <strong>5</strong>
            <span>Nodes</span>
          </div>
          <div className="h2-bg-stat">
            <strong>2</strong>
            <span>Pinned</span>
          </div>
          <div className="h2-bg-stat">
            <strong>517</strong>
            <span>Days left</span>
          </div>
        </div>

        <div className="h2-bg-exec-filters">
          <span className="h2-bg-pill h2-bg-pill--active">All <em>5</em></span>
          <span className="h2-bg-pill">Pinned <em>2</em></span>
          <span className="h2-bg-pill">Due <em>3</em></span>
        </div>

        <div className="h2-bg-exec-list">
          <div className="h2-bg-exec-item">
            <span className="h2-bg-check h2-bg-check--done">✓</span>
            <div className="h2-bg-exec-body">
              <span className="h2-bg-strike">Write hero copy</span>
              <span className="h2-bg-exec-src">Landing page</span>
            </div>
          </div>
          <div className="h2-bg-exec-item h2-bg-exec-item--focus">
            <span className="h2-bg-check" />
            <div className="h2-bg-exec-body">
              <span>Design pricing card</span>
              <span className="h2-bg-exec-src">Landing page</span>
            </div>
            <span className="h2-bg-pin">📌</span>
          </div>
          <div className="h2-bg-exec-item">
            <span className="h2-bg-check" />
            <div className="h2-bg-exec-body">
              <span>Wire up Stripe checkout</span>
              <span className="h2-bg-exec-src">Stripe</span>
            </div>
            <span className="h2-bg-pin">📌</span>
          </div>
          <div className="h2-bg-exec-item">
            <span className="h2-bg-check" />
            <div className="h2-bg-exec-body">
              <span>Draft onboarding emails</span>
              <span className="h2-bg-exec-src">Onboarding</span>
            </div>
          </div>
        </div>

        <div className="h2-bg-exec-log">
          <span className="h2-bg-exec-log-label">Quick log</span>
          <div className="h2-bg-exec-log-input">
            <span>What did you ship today?</span>
            <span className="h2-bg-exec-log-cta">Save →</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function HabitsModeGraphic() {
  return (
    <div className="h2-bg-mock">
      <div className="h2-bg-mock-head">
        <div className="h2-bg-mock-dots">
          <span className="h2-bg-mock-dot" />
          <span className="h2-bg-mock-dot" />
          <span className="h2-bg-mock-dot" />
        </div>
        <span className="h2-bg-mock-title">↻ Habits</span>
        <span className="h2-bg-mock-meta">This week</span>
      </div>
      <div className="h2-bg-mock-body h2-bg-mock-body--habits">
        <div className="h2-bg-habits-habit">
          <div className="h2-bg-habits-row">
            <span className="h2-bg-habits-name">Deep work block</span>
            <span className="h2-bg-habits-streak">🔥 12d</span>
          </div>
          <div className="h2-bg-habits-grid">
            {['M','T','W','T','F','S','S'].map((d, i) => (
              <div key={i} className={`h2-bg-habits-cell ${i < 5 ? 'h2-bg-habits-cell--done' : ''}`}>
                <span className="h2-bg-habits-day">{d}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="h2-bg-habits-habit">
          <div className="h2-bg-habits-row">
            <span className="h2-bg-habits-name">Read 20 pages</span>
            <span className="h2-bg-habits-streak">🔥 6d</span>
          </div>
          <div className="h2-bg-habits-grid">
            {['M','T','W','T','F','S','S'].map((d, i) => (
              <div key={i} className={`h2-bg-habits-cell ${[0,1,3,4].includes(i) ? 'h2-bg-habits-cell--done' : ''}`}>
                <span className="h2-bg-habits-day">{d}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="h2-bg-habits-habit">
          <div className="h2-bg-habits-row">
            <span className="h2-bg-habits-name">No social after 9pm</span>
            <span className="h2-bg-habits-streak">🔥 3d</span>
          </div>
          <div className="h2-bg-habits-grid">
            {['M','T','W','T','F','S','S'].map((d, i) => (
              <div key={i} className={`h2-bg-habits-cell ${[0,1,2].includes(i) ? 'h2-bg-habits-cell--done' : ''}`}>
                <span className="h2-bg-habits-day">{d}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="h2-bg-habits-footer">
          <span className="h2-bg-pill h2-bg-pill--active">Week</span>
          <span className="h2-bg-pill">Month</span>
          <span className="h2-bg-pill">All time</span>
        </div>
      </div>
    </div>
  );
}

const BRIDGE_MODES = [
  {
    tab: 'Plan',
    title: 'Decide what to do.',
    desc: 'Build the tree. Set milestones. Break the vision down until every piece is small enough to act on.',
    Graphic: PlanModeGraphic,
  },
  {
    tab: 'Execute',
    title: 'Get things done.',
    desc: 'One focused page. Every actionable item, ready to ship. Pin what matters, check it off, log what you did.',
    Graphic: ExecuteModeGraphic,
  },
  {
    tab: 'Habits',
    title: 'Build the discipline behind the plan.',
    desc: 'Track the daily habits that make the vision possible. Streaks, weekly grids, all tied to the same goal.',
    Graphic: HabitsModeGraphic,
  },
];

function BridgeSwitcher() {
  const [active, setActive] = useState(0);
  const Graphic = BRIDGE_MODES[active].Graphic;
  return (
    <div className="h2-bg-switcher">
      <div className="h2-bg-tabs" role="tablist">
        {BRIDGE_MODES.map((m, i) => (
          <button
            key={m.tab}
            role="tab"
            className={`h2-bg-tab ${active === i ? 'h2-bg-tab--active' : ''}`}
            onClick={() => setActive(i)}
          >
            {m.tab}
          </button>
        ))}
      </div>

      <div className="h2-bg-panel">
        <div className="h2-bg-panel-text" key={`bt-${active}`}>
          <span className="h2-bridge-tag">{BRIDGE_MODES[active].tab.toUpperCase()}</span>
          <h3>{BRIDGE_MODES[active].title}</h3>
          <p>{BRIDGE_MODES[active].desc}</p>
        </div>
        <div className="h2-bg-panel-graphic" key={`bg-${active}`}>
          <Graphic />
        </div>
      </div>
    </div>
  );
}

// ── Growing tree (hero interactive) ────────────────────────────────────────

const TREE_STAGES = [
  { key: 'seed',    label: 'Plant your vision',     btn: 'Add milestones' },
  { key: 'sprout',  label: 'Add milestones',        btn: 'Break into actions' },
  { key: 'sapling', label: 'Break into actions',    btn: 'Add sub-steps' },
  { key: 'tree',    label: 'Walk the path',         btn: 'Make it happen' },
  { key: 'bloom',   label: 'Vision becomes real',   btn: 'Plant another' },
];

// ── Breakdown graphic: chaos → calm particle swarm (interactive) ───────────
// Hundreds of jittering dots (noise/overwhelm) ease into a calm ascending
// path of milestone nodes as you drag. Stillness + order = calm.
const SWARM_NODES = [
  { x: 0.10, y: 0.82, label: 'First post' },
  { x: 0.31, y: 0.65, label: '1K followers' },
  { x: 0.52, y: 0.49, label: '10K followers' },
  { x: 0.73, y: 0.33, label: 'Brand deals' },
  { x: 0.91, y: 0.19, label: 'Full-time' },
];

const sLerp = (a, b, t) => a + (b - a) * t;
const sEase = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

function makeSwarm() {
  const parts = [];
  const rand = (a, b) => a + Math.random() * (b - a);

  // milestone node particles
  SWARM_NODES.forEach((n) => {
    parts.push({
      kind: 'node', cx: Math.random(), cy: Math.random(),
      tx: n.x, ty: n.y, r: rand(4.5, 6), ph: rand(0, 6.28), sp: rand(0.6, 1.1), amp: rand(0.02, 0.035),
    });
  });

  // thread particles sampled along the path
  const SEG = SWARM_NODES.length - 1;
  for (let i = 0; i < 140; i++) {
    const u = i / 139;
    const f = u * SEG;
    const seg = Math.min(SEG - 1, Math.floor(f));
    const lt = f - seg;
    const a = SWARM_NODES[seg], b = SWARM_NODES[seg + 1];
    let tx = sLerp(a.x, b.x, lt), ty = sLerp(a.y, b.y, lt);
    // perpendicular scatter so it reads as a soft band of dots
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const scatter = rand(-0.018, 0.018);
    tx += (-dy / len) * scatter;
    ty += (dx / len) * scatter;
    parts.push({
      kind: 'thread', cx: Math.random(), cy: Math.random(),
      tx, ty, r: rand(1.4, 2.6), ph: rand(0, 6.28), sp: rand(0.5, 1.3), amp: rand(0.02, 0.045),
    });
  }

  // noise particles that drift away as calm sets in
  for (let i = 0; i < 46; i++) {
    parts.push({
      kind: 'noise', cx: Math.random(), cy: Math.random(),
      tx: rand(-0.15, 1.15), ty: rand(-0.15, 1.15), r: rand(1.2, 2.2),
      ph: rand(0, 6.28), sp: rand(0.6, 1.6), amp: rand(0.03, 0.06),
    });
  }
  return parts;
}

function BreakdownGraphic() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const knobRef = useRef(null);
  const fillRef = useRef(null);
  const readoutRef = useRef(null);
  const partsRef = useRef(null);
  const pRef = useRef(0);
  const touchedRef = useRef(false);
  const [touched, setTouched] = useState(false);
  const [dragging, setDragging] = useState(false);

  if (!partsRef.current) partsRef.current = makeSwarm();

  const setFromX = (clientX) => {
    const el = wrapRef.current;
    if (!el || clientX == null) return;
    const r = el.getBoundingClientRect();
    pRef.current = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
  };

  const onDown = (e) => {
    setDragging(true);
    touchedRef.current = true;
    if (!touched) setTouched(true);
    setFromX(e.clientX ?? e.touches?.[0]?.clientX);
  };

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => setFromX(e.clientX ?? e.touches?.[0]?.clientX);
    const up = () => setDragging(false);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [dragging]);

  // canvas render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');
    let raf, roRaf, w = 0, h = 0, dpr = 1;

    const resize = () => {
      const nw = wrap.clientWidth;
      const nh = wrap.clientHeight;
      if (!nw || !nh || (nw === w && nh === h)) return; // skip no-op resizes
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = nw;
      h = nh;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    // Defer observer work out of the callback to avoid the
    // "ResizeObserver loop completed with undelivered notifications" warning.
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(roRaf);
      roRaf = requestAnimationFrame(resize);
    });
    ro.observe(wrap);

    const CH = [120, 132, 150];   // chaos colour (cool slate)
    const CA = [241, 117, 1];     // calm colour (orange)
    const start = performance.now();

    const draw = (now) => {
      const t = (now - start) / 1000;

      // progress is user-controlled only — particles keep jittering at rest
      const p = pRef.current;
      const e = sEase(p);

      ctx.clearRect(0, 0, w, h);

      // calm path line through nodes
      if (e > 0.05) {
        ctx.beginPath();
        SWARM_NODES.forEach((n, i) => {
          const X = n.x * w, Y = n.y * h;
          i === 0 ? ctx.moveTo(X, Y) : ctx.lineTo(X, Y);
        });
        ctx.strokeStyle = `rgba(241,117,1,${e * 0.35})`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      const parts = partsRef.current;
      for (let i = 0; i < parts.length; i++) {
        const pt = parts[i];
        const isNoise = pt.kind === 'noise';
        const jAmp = (1 - e) * pt.amp;
        const jx = jAmp * Math.sin(t * pt.sp + pt.ph);
        const jy = jAmp * Math.cos(t * pt.sp * 1.1 + pt.ph);
        const nx = (sLerp(pt.cx, pt.tx, e) + jx) * w;
        const ny = (sLerp(pt.cy, pt.ty, e) + jy) * h;

        let alpha, col, rad = pt.r;
        if (isNoise) {
          alpha = (1 - e) * 0.5;
          col = CH;
        } else {
          alpha = sLerp(0.45, 0.95, e);
          col = [
            Math.round(sLerp(CH[0], CA[0], e)),
            Math.round(sLerp(CH[1], CA[1], e)),
            Math.round(sLerp(CH[2], CA[2], e)),
          ];
          if (pt.kind === 'node') {
            rad = pt.r * sLerp(0.8, 1.25, e);
          }
        }
        if (alpha <= 0.01) continue;

        if (pt.kind === 'node' && e > 0.4) {
          ctx.beginPath();
          ctx.arc(nx, ny, rad * 2.4, 0, 6.2832);
          ctx.fillStyle = `rgba(241,117,1,${(e - 0.4) * 0.18})`;
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(nx, ny, rad, 0, 6.2832);
        ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${alpha})`;
        ctx.fill();
      }

      // node labels at high calm
      if (e > 0.6) {
        const la = (e - 0.6) / 0.4;
        ctx.font = '600 11px "DM Sans", sans-serif';
        ctx.fillStyle = `rgba(250,247,240,${la * 0.85})`;
        ctx.textAlign = 'center';
        SWARM_NODES.forEach((n) => {
          ctx.fillText(n.label, n.x * w, n.y * h - 14);
        });
      }

      // sync slider UI directly (no per-frame React render)
      const pct = (pRef.current * 100).toFixed(1) + '%';
      if (knobRef.current) knobRef.current.style.left = pct;
      if (fillRef.current) fillRef.current.style.width = pct;
      if (readoutRef.current) {
        const pp = pRef.current;
        readoutRef.current.textContent = pp < 0.34 ? 'Overwhelm' : pp < 0.7 ? 'Settling' : 'Calm';
        readoutRef.current.style.color = pp < 0.34 ? 'rgba(150,160,180,0.9)' : pp < 0.7 ? 'rgba(250,247,240,0.7)' : '#f17501';
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); cancelAnimationFrame(roRaf); ro.disconnect(); };
  }, []);

  return (
    <div className="h2-swarm">
      <div
        className="h2-swarm-stage"
        ref={wrapRef}
        onPointerDown={onDown}
        role="slider"
        aria-label="Drag to settle the chaos into a calm plan"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pRef.current * 100)}
        tabIndex={0}
        onKeyDown={(ev) => {
          if (ev.key === 'ArrowRight') { touchedRef.current = true; setTouched(true); pRef.current = Math.min(1, pRef.current + 0.08); }
          if (ev.key === 'ArrowLeft')  { touchedRef.current = true; setTouched(true); pRef.current = Math.max(0, pRef.current - 0.08); }
        }}
      >
        <canvas ref={canvasRef} className="h2-swarm-canvas" />
        <span className="h2-swarm-readout" ref={readoutRef}>Overwhelm</span>
        <span className="h2-swarm-label h2-swarm-label--left">Noise</span>
        <span className="h2-swarm-label h2-swarm-label--right">A calm path</span>
      </div>

      <div className="h2-swarm-track" aria-hidden="true">
        <div className="h2-swarm-track-fill" ref={fillRef} />
        <div
          className={`h2-swarm-knob${dragging ? ' is-grab' : ''}${touched ? '' : ' is-hint'}`}
          ref={knobRef}
          onPointerDown={onDown}
        />
      </div>
      <p className="h2-swarm-hint">{touched ? 'Drag to settle the chaos' : '← drag to settle the chaos →'}</p>
    </div>
  );
}

// ── AI showcase: one sentence → Dmyor Assist builds across the workspace ─────
const AI_CARDS = [
  { kind: 'milestone', badge: 'milestone', accent: '#f17501', text: 'Get visibly leaner in 16 weeks', sub: 'Plan · milestone' },
  { kind: 'habit',     badge: '↻ habit · daily', accent: '#22d3ee', text: 'Train 45 minutes', sub: 'Habits · tracked with streaks' },
  { kind: 'todo',      badge: '✓ todo', accent: '#a78bfa', text: 'Meal-prep on Sunday', sub: 'Execute · today list' },
  { kind: 'doc',       badge: '◆ document', accent: '#fbbf24', text: '16-Week Training Plan', sub: 'Library · working doc' },
];

function AiAssistShowcase() {
  const ref = useRef(null);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let running = false;
    let timer;
    // delay spent IN each phase before advancing
    const DUR = [900, 850, 1000, 560, 560, 560, 560, 1700, 2400];
    let p = 0;
    const run = () => {
      setPhase(p);
      timer = setTimeout(() => { p = (p + 1) % DUR.length; run(); }, DUR[p]);
    };
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !running) { running = true; run(); }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => { io.disconnect(); clearTimeout(timer); };
  }, []);

  const userIn  = phase >= 1;
  const typing  = phase === 2;
  const replyIn = phase >= 3;
  const added   = phase >= 8;

  return (
    <div className="h2-ai" ref={ref}>
      <div className="h2-ai-window">
        <div className="h2-ai-bar">
          <span className="h2-ai-dot" /><span className="h2-ai-dot" /><span className="h2-ai-dot" />
          <span className="h2-ai-bar-title">✦ Dmyor Assist</span>
        </div>

        <div className="h2-ai-thread">
          <div className={`h2-ai-msg h2-ai-msg--user${userIn ? ' is-in' : ''}`}>
            <div className="h2-ai-bubble h2-ai-bubble--user">
              Help me get in the best shape of my life.
            </div>
          </div>

          {typing && (
            <div className="h2-ai-msg h2-ai-msg--ai is-in">
              <div className="h2-ai-bubble h2-ai-typing"><span /><span /><span /></div>
            </div>
          )}

          <div className={`h2-ai-msg h2-ai-msg--ai${replyIn && !typing ? ' is-in' : ''}`} style={{ display: replyIn && !typing ? 'flex' : 'none' }}>
            <div className="h2-ai-bubble">
              Here's how I'd set it up across your plan — pick what you want:
            </div>
          </div>

          <div className="h2-ai-cards">
            {AI_CARDS.map((c, i) => {
              const cardIn = phase >= 4 + i;
              return (
                <div
                  key={c.kind}
                  className={`h2-ai-card${cardIn ? ' is-in' : ''}${added ? ' is-added' : ''}`}
                  style={{ '--ac': c.accent }}
                >
                  <span className="h2-ai-card-badge">{c.badge}</span>
                  <span className="h2-ai-card-text">{c.text}</span>
                  <span className="h2-ai-card-sub">{c.sub}</span>
                  <span className="h2-ai-card-check">
                    <svg viewBox="0 0 20 20" width="13" height="13"><path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    Added
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Explore: the intent archive (animated wall of visions) ─────────────────
const ARCHIVE_CARDS = [
  { intent: 'Build a SaaS to $10K MRR', who: '18-month vision', year: "'24", status: 'made',     steps: 4, done: 4 },
  { intent: 'Run my first marathon', who: '1-year vision', year: "'25", status: 'building', steps: 4, done: 2 },
  { intent: 'Leave my job, go full-time on my art', who: '2-year vision', year: "'24", status: 'made', steps: 5, done: 5 },
  { intent: 'Write and publish a novel', who: '3-year vision', year: "'25", status: 'building', steps: 5, done: 1 },
  { intent: 'Get fluent in Japanese', who: '2-year vision', year: "'23", status: 'letgo', steps: 4, done: 1 },
  { intent: 'Save my first $100K', who: '4-year vision', year: "'25", status: 'building', steps: 4, done: 3 },
];
const ARCHIVE_STATUS = {
  made:     { label: 'Now a reality', cls: 'made' },
  building: { label: 'In progress', cls: 'building' },
  letgo:    { label: 'Let go', cls: 'letgo' },
};

function ExploreArchive() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className={`h2-arch-wall ${seen ? 'is-in' : ''}`} ref={ref}>
      {ARCHIVE_CARDS.map((c, i) => {
        const s = ARCHIVE_STATUS[c.status];
        return (
          <div key={i} className={`h2-arch-card h2-arch-card--${s.cls}`} style={{ '--i': i }}>
            <div className="h2-arch-card-top">
              <span className="h2-arch-tag">VISION</span>
              <span className="h2-arch-year">{c.year}</span>
            </div>
            <p className="h2-arch-intent">{c.intent}</p>
            <span className="h2-arch-who">{c.who}</span>
            <div className="h2-arch-path" aria-hidden="true">
              {Array.from({ length: c.steps }).map((_, k) => (
                <span key={k} className={`h2-arch-dot${k < c.done ? ' is-done' : ''}`} />
              ))}
            </div>
            <span className={`h2-arch-stamp h2-arch-stamp--${s.cls}`}>{s.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Give it 1-5 years: a dream broken into a climbable staircase ───────────
const HORIZON_STEPS = [
  { k: 'Today',      t: 'Write it down',          s: 'One honest sentence.' },
  { k: 'Horizon',    t: 'Give it 1 to 5 years',   s: 'A real finish line, not someday.' },
  { k: 'Break down', t: 'Into milestones',        s: 'The few that prove progress.' },
  { k: 'Smaller',    t: 'Into a first action',    s: 'Small enough to start this week.' },
  { k: 'The dream',  t: 'Becomes real',           s: 'One step at a time, for years.', dream: true },
];

function HorizonStairs() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className={`h2-stairs ${seen ? 'is-in' : ''}`} ref={ref}>
      <div className="h2-stairs-pills">
        {[1, 2, 3, 4, 5].map(y => (
          <span key={y} className={`h2-stairs-pill${y === 3 ? ' is-on' : ''}`}>{y}y</span>
        ))}
        <span className="h2-stairs-pills-label">pick your horizon</span>
      </div>

      <div className="h2-stairs-rail">
        {HORIZON_STEPS.map((step, i) => (
          <div
            key={i}
            className={`h2-stair${step.dream ? ' h2-stair--dream' : ''}`}
            style={{ '--i': i }}
          >
            <span className="h2-stair-tread" />
            <div className="h2-stair-card">
              <span className="h2-stair-k">{step.k}</span>
              <span className="h2-stair-t">{step.t}</span>
              <span className="h2-stair-s">{step.s}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GrowingTree() {
  const [stage, setStage] = useState(0);
  const advance = () => setStage(s => (s + 1) % TREE_STAGES.length);

  return (
    <div className="h2-tree-wrap">
      <div className="h2-tree-stage-label" key={stage}>
        <span className="h2-tree-stage-num">0{stage + 1}</span>
        <span className="h2-tree-stage-text">{TREE_STAGES[stage].label}</span>
      </div>

      <div className={`h2-tree-canvas h2-tree-canvas--s${stage}`}>
        <svg viewBox="0 0 360 400" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          {/* Soft glow halo */}
          <defs>
            <radialGradient id="treeGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(201,137,105,0.18)" />
              <stop offset="100%" stopColor="rgba(201,137,105,0)" />
            </radialGradient>
            <linearGradient id="trunkGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8b6347" />
              <stop offset="100%" stopColor="#5e3f2a" />
            </linearGradient>
          </defs>

          <circle className="h2-tree-glow" cx="180" cy="220" r="170" fill="url(#treeGlow)" />

          {/* Ground hill */}
          <ellipse className="h2-tree-soil" cx="180" cy="358" rx="130" ry="16" fill="#3a2718" />
          <ellipse className="h2-tree-soil2" cx="180" cy="356" rx="100" ry="9" fill="#5a3c25" />

          {/* SEED — only on stage 0 */}
          <g className="h2-tree-el h2-tree-seed">
            <ellipse cx="180" cy="350" rx="9" ry="11" fill="#c98969" />
            <path d="M180,340 Q175,335 178,332" stroke="#7a4a32" strokeWidth="2" fill="none" strokeLinecap="round" />
            <ellipse cx="178" cy="346" rx="2" ry="3" fill="#a36b4d" opacity="0.7" />
          </g>

          {/* TRUNK — from seed up */}
          <path
            className="h2-tree-el h2-tree-trunk"
            d="M180,358 C180,340 178,300 182,260 C184,220 180,180 180,140"
            stroke="url(#trunkGrad)"
            strokeWidth="9"
            strokeLinecap="round"
            fill="none"
          />

          {/* SPROUT LEAVES (stage 1 first) */}
          <g className="h2-tree-el h2-tree-sprout-leaves">
            <ellipse cx="158" cy="290" rx="22" ry="11" fill="#aabba0" transform="rotate(-30 158 290)" />
            <ellipse cx="202" cy="290" rx="22" ry="11" fill="#9caf91" transform="rotate(30 202 290)" />
          </g>

          {/* MAIN BRANCHES (stage 2+) */}
          <path
            className="h2-tree-el h2-tree-branch h2-tree-branch--l1"
            d="M180,230 Q140,210 110,180"
            stroke="#6b4a32" strokeWidth="5" fill="none" strokeLinecap="round"
          />
          <path
            className="h2-tree-el h2-tree-branch h2-tree-branch--r1"
            d="M180,230 Q220,210 250,180"
            stroke="#6b4a32" strokeWidth="5" fill="none" strokeLinecap="round"
          />
          <path
            className="h2-tree-el h2-tree-branch h2-tree-branch--c1"
            d="M180,180 L180,140"
            stroke="#6b4a32" strokeWidth="4.5" fill="none" strokeLinecap="round"
          />

          {/* SECONDARY BRANCHES (stage 3+) */}
          <path
            className="h2-tree-el h2-tree-branch h2-tree-branch--l2"
            d="M125,190 Q100,165 90,135"
            stroke="#7a543a" strokeWidth="3.5" fill="none" strokeLinecap="round"
          />
          <path
            className="h2-tree-el h2-tree-branch h2-tree-branch--l3"
            d="M125,190 Q140,160 145,135"
            stroke="#7a543a" strokeWidth="3.5" fill="none" strokeLinecap="round"
          />
          <path
            className="h2-tree-el h2-tree-branch h2-tree-branch--r2"
            d="M235,190 Q260,165 270,135"
            stroke="#7a543a" strokeWidth="3.5" fill="none" strokeLinecap="round"
          />
          <path
            className="h2-tree-el h2-tree-branch h2-tree-branch--r3"
            d="M235,190 Q220,160 215,135"
            stroke="#7a543a" strokeWidth="3.5" fill="none" strokeLinecap="round"
          />
          <path
            className="h2-tree-el h2-tree-branch h2-tree-branch--c2"
            d="M180,150 Q165,130 158,108"
            stroke="#7a543a" strokeWidth="3" fill="none" strokeLinecap="round"
          />
          <path
            className="h2-tree-el h2-tree-branch h2-tree-branch--c3"
            d="M180,150 Q195,130 202,108"
            stroke="#7a543a" strokeWidth="3" fill="none" strokeLinecap="round"
          />

          {/* LEAVES (stage 3+) — clusters at branch tips */}
          <g className="h2-tree-el h2-tree-leaves">
            <ellipse cx="90" cy="130" rx="22" ry="16" fill="#aabba0" />
            <ellipse cx="78" cy="120" rx="14" ry="11" fill="#9caf91" />
            <ellipse cx="105" cy="115" rx="13" ry="10" fill="#bccbb1" />

            <ellipse cx="148" cy="130" rx="20" ry="14" fill="#9caf91" />
            <ellipse cx="160" cy="120" rx="13" ry="10" fill="#bccbb1" />

            <ellipse cx="270" cy="130" rx="22" ry="16" fill="#aabba0" />
            <ellipse cx="282" cy="120" rx="14" ry="11" fill="#bccbb1" />
            <ellipse cx="255" cy="115" rx="13" ry="10" fill="#9caf91" />

            <ellipse cx="212" cy="130" rx="20" ry="14" fill="#9caf91" />
            <ellipse cx="200" cy="120" rx="13" ry="10" fill="#bccbb1" />

            <ellipse cx="158" cy="100" rx="16" ry="12" fill="#aabba0" />
            <ellipse cx="202" cy="100" rx="16" ry="12" fill="#bccbb1" />
            <ellipse cx="180" cy="90" rx="20" ry="14" fill="#9caf91" />
            <ellipse cx="170" cy="78" rx="12" ry="9" fill="#bccbb1" />
            <ellipse cx="192" cy="80" rx="13" ry="10" fill="#aabba0" />
          </g>

          {/* BLOOMS (stage 4) — fruits/flowers */}
          <g className="h2-tree-el h2-tree-blooms">
            <circle cx="92" cy="118" r="5" fill="#c98969" />
            <circle cx="92" cy="118" r="2" fill="#fff" opacity="0.45" />

            <circle cx="270" cy="118" r="5" fill="#c98969" />
            <circle cx="270" cy="118" r="2" fill="#fff" opacity="0.45" />

            <circle cx="180" cy="82" r="6" fill="#c98969" />
            <circle cx="180" cy="82" r="2.5" fill="#fff" opacity="0.5" />

            <circle cx="150" cy="125" r="4" fill="#d9967a" />
            <circle cx="208" cy="125" r="4" fill="#d9967a" />

            <circle cx="160" cy="100" r="3.5" fill="#c98969" />
            <circle cx="200" cy="100" r="3.5" fill="#c98969" />
          </g>

          {/* Falling petals (bloom stage) */}
          <g className="h2-tree-el h2-tree-petals">
            <circle cx="60" cy="200" r="3" fill="#c98969" className="h2-tree-petal h2-tree-petal--1" />
            <circle cx="310" cy="220" r="2.5" fill="#d9967a" className="h2-tree-petal h2-tree-petal--2" />
            <circle cx="90" cy="280" r="2.5" fill="#c98969" className="h2-tree-petal h2-tree-petal--3" />
          </g>
        </svg>
      </div>

      <button className="h2-tree-water" onClick={advance}>
        <svg className="h2-tree-drop" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3 C 7 11, 5 14, 5 17 a 7 7 0 0 0 14 0 c 0 -3 -2 -6 -7 -14 Z"
            fill="currentColor"
          />
        </svg>
        <span>{TREE_STAGES[stage].btn}</span>
      </button>

      <div className="h2-tree-progress" role="presentation">
        {TREE_STAGES.map((_, i) => (
          <span key={i} className={`h2-tree-dot ${i <= stage ? 'h2-tree-dot--on' : ''}`} />
        ))}
      </div>
    </div>
  );
}

const Home2 = () => {
  useEffect(() => {
    const reveal = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            reveal.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    document.querySelectorAll('.h2-reveal').forEach((el) => reveal.observe(el));

    const sectionSeen = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('h2-section-seen');
            sectionSeen.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    document
      .querySelectorAll('.h2-hero, .h2-fns, .h2-break, .h2-bridge, .h2-aiwrap, .h2-archive, .h2-horizon, .h2-journey, .h2-closing')
      .forEach((el) => sectionSeen.observe(el));

    return () => {
      reveal.disconnect();
      sectionSeen.disconnect();
    };
  }, []);

  return (
    <div className="h2-root">
      <Helmet>
        <title>dmyor | You know what you want. We help you see the path.</title>
        <meta name="description" content="For the version of you who keeps almost starting. dmyor turns the thing you've been carrying around into a plan you can actually walk." />
      </Helmet>

      {/* ── HERO ── */}
      <section className="h2-hero">
        <div className="h2-hero-inner h2-reveal h2-reveal--stagger">
          <div className="h2-hero-main">
            <Link to="/intent-archive" className="h2-hero-archive-pill">
              your vision will be preserved for next 100 years. See how
              <span className="h2-hero-archive-arrow">→</span>
            </Link>

            <h1 className="h2-hero-title">
              A new gen planning system,<br />
              <span className="h2-accent">designed to make your visions real.</span>
            </h1>
            <p className="h2-hero-sub">
              Type one sentence. dmyor turns it into milestones, daily steps, and a path you can walk — with AI that knows your whole plan. Every dream, preserved forever.
            </p>
            <div className="h2-hero-cta">
              <Link to="/dashboard/planning/new" className="h2-btn-primary">Start building</Link>
              <Link to="/intent-archive" className="h2-btn-ghost">The Intent Archive →</Link>
            </div>
          </div>

          <aside className="h2-hero-aside">
            <GrowingTree />
          </aside>
        </div>

        <div className="h2-hero-band">
          <span>One sentence in.</span>
          <span className="h2-hero-band-sep">/</span>
          <span>A full plan out.</span>
          <span className="h2-hero-band-sep">/</span>
          <span>Archived for life.</span>
        </div>
      </section>

      {/* ── INTERACTIVE DEMO ── */}
      <div id="h2-demo">
        <InteractiveDemo />
      </div>

      {/* ── FUNCTIONS ── */}
      <section className="h2-fns">
        <div className="h2-fns-inner">
          <div className="h2-fns-head h2-reveal">
            <h2 className="h2-section-heading">
              Built to pull the thing <span className="h2-accent">out of your head.</span>
            </h2>
            <p className="h2-section-body">
              Four parts working together so the gap between knowing what you want and actually doing it quietly closes.
            </p>
          </div>

          <div className="h2-reveal">
            <FunctionsSwitcher />
          </div>
        </div>
      </section>

      {/* ── BREAKDOWN: tangled thoughts → structured plan ── */}
      <section className="h2-break">
        <div className="h2-break-inner">
          <div className="h2-break-head h2-reveal">
            <h2 className="h2-section-heading">
              The same chaos, <span className="h2-accent">finally in order.</span>
            </h2>
            <p className="h2-section-body">
              A big goal lives in your head as a tangle, every piece shouting at once. Dmyor takes it apart: vision into milestones, milestones into actions small enough to start today.
            </p>
          </div>
          <div className="h2-reveal">
            <BreakdownGraphic />
          </div>
        </div>
      </section>

      {/* ── BRIDGE: plan ↔ execute (toggleable) ── */}
      <section className="h2-bridge">
        <div className="h2-bridge-inner h2-reveal h2-reveal--stagger">
          <h2 className="h2-bridge-heading">
            One system. <span className="h2-accent">Three modes.</span>
          </h2>
          <p className="h2-bridge-sub">
            Plan decides what to do. Execute keeps you focused on what matters most. Habits make sure you show up every day to do it.
          </p>

          <div style={{ width: '100%' }}>
            <BridgeSwitcher />
          </div>
        </div>
      </section>

      {/* ── AI: tell it, it builds ── */}
      <section className="h2-aiwrap">
        <div className="h2-aiwrap-inner">
          <div className="h2-aiwrap-head h2-reveal">
            <h2 className="h2-section-heading">
              Or just <span className="h2-accent">tell the AI.</span>
            </h2>
            <p className="h2-section-body">
              Dmyor Assist lives inside your plan. Say what you want in plain words and it builds it — milestones to break down, habits to track, todos for today, full documents to work in. You stay in control: it suggests, you approve.
            </p>
          </div>
          <div className="h2-reveal">
            <AiAssistShowcase />
          </div>
        </div>
      </section>

      {/* ── EXPLORE: the intent archive ── */}
      <section className="h2-archive">
        <div className="h2-archive-inner">
          <div className="h2-archive-head h2-reveal">
            <span className="h2-archive-eyebrow">Explore</span>
            <h2 className="h2-section-heading">
              The intent archive <span className="h2-accent">of the world.</span>
            </h2>
            <p className="h2-section-body">
              Every vision written on dmyor can be made public, and together they form something that has never existed: a living record of what people actually set out to do. What they wanted. How they planned to get there. And whether they made it real.
            </p>
          </div>

          <div className="h2-reveal">
            <ExploreArchive />
          </div>

          <div className="h2-archive-foot h2-reveal">
            <p className="h2-archive-foot-line">
              Not polished highlight reels. The real thing: intentions on the record, outcomes and all.
              <span className="h2-archive-foot-note">Every post stays anonymous until you choose to release it, the moment your vision becomes real.</span>
            </p>
            <Link to="/s" className="h2-archive-cta">Join the waitlist →</Link>
          </div>
        </div>
      </section>

      {/* ── GIVE IT 1-5 YEARS ── */}
      <section className="h2-horizon">
        <div className="h2-horizon-inner">
          <div className="h2-horizon-head h2-reveal">
            <span className="h2-horizon-eyebrow">Give it 1 to 5 years</span>
            <h2 className="h2-section-heading">
              The dream isn't too big. <span className="h2-accent">It's just never been broken down.</span>
            </h2>
            <p className="h2-section-body">
              You've carried it for years. The business, the trip, the body, the book. It never started because every time you looked at it, it was too big to begin. So it stayed a someday. dmyor takes that someday, gives it a real horizon, and breaks it into the first small step you can take this week. Then the next. That's how dreams stop being dreams.
            </p>
          </div>

          <div className="h2-reveal">
            <HorizonStairs />
          </div>

          <div className="h2-horizon-foot h2-reveal">
            <p className="h2-horizon-foot-line">
              Most dreams don't die from lack of wanting. They die in the gap between wanting and starting.
            </p>
            <Link to="/blog/why-your-dreams-stay-dreams" className="h2-horizon-link">
              Read: why unplanned dreams stay dreams →
            </Link>
          </div>
        </div>
      </section>

      {/* ── SYSTEM vs CHAT ── */}
      <section className="h2-journey">
        <div className="h2-journey-inner">
          <div className="h2-journey-head h2-reveal">
            <h2 className="h2-section-heading">
              Most AI stops at the answer. <span className="h2-accent">dmyor lets you act on it.</span>
            </h2>
            <p className="h2-section-body">
              Other AI tools hand you a response and end the conversation. In dmyor that response becomes something you can use: break it down, track it, log progress, and run it day to day. Not a reply you read once, but a plan you actually work inside. The AI is here to help you move it, not to hand you a document and leave.
            </p>
          </div>

          <div className="h2-cap-grid h2-reveal h2-reveal--stagger">
            <div className="h2-cap">
              <span className="h2-cap-num">01</span>
              <h3 className="h2-cap-title">Break it down</h3>
              <p className="h2-cap-desc">Your vision becomes milestones and actions small enough to actually start.</p>
            </div>
            <div className="h2-cap">
              <span className="h2-cap-num">02</span>
              <h3 className="h2-cap-title">Track every piece</h3>
              <p className="h2-cap-desc">Each node carries a status you move from standby to active to done.</p>
            </div>
            <div className="h2-cap">
              <span className="h2-cap-num">03</span>
              <h3 className="h2-cap-title">Log your progress</h3>
              <p className="h2-cap-desc">Post updates on any node. A real record builds as you move, not after.</p>
            </div>
            <div className="h2-cap">
              <span className="h2-cap-num">04</span>
              <h3 className="h2-cap-title">Run it daily</h3>
              <p className="h2-cap-desc">Execute turns the whole plan into one focused page of today's work.</p>
            </div>
            <div className="h2-cap h2-cap--ai">
              <span className="h2-cap-num">✦</span>
              <h3 className="h2-cap-title">AI that supports it</h3>
              <p className="h2-cap-desc">Plugged into your whole plan, it suggests and drafts. You stay in control.</p>
            </div>
          </div>

          <div className="h2-cap-foot h2-reveal">
            <Link to="/dashboard/planning/new" className="h2-journey-cta">Plan something you can run →</Link>
          </div>
        </div>
      </section>

      {/* ── CLOSING ── */}
      <section className="h2-closing">
        <div className="h2-closing-inner h2-reveal">
          <h2 className="h2-closing-title">
            The thing you keep meaning to do?<br />
            <span className="h2-accent">Put it on the page tonight.</span>
          </h2>
          <p className="h2-closing-sub">
            It takes one sentence. dmyor builds the rest with you. The hardest part is admitting you're going to start.
          </p>
          <Link to="/dashboard/planning/new" className="h2-btn-primary h2-btn-large">Open the planner</Link>
          <p className="h2-closing-note">Free while in beta. Anonymous by default.</p>
        </div>
      </section>
    </div>
  );
};

export default Home2;
